import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useAuth, API } from "../lib/auth";
import { Plus, Loader2, ShieldCheck, Download, Trash2 } from "lucide-react";

const ALL_PERMS = [
  { key: "can_manage_users", label: "Manage users" },
  { key: "can_manage_jobs", label: "Manage jobs" },
  { key: "can_manage_employers", label: "Manage employers" },
  { key: "can_manage_news", label: "Manage news" },
  { key: "can_view_analytics", label: "View analytics" },
];

export default function AdminStaffPanel() {
  const { authHeader, user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", permissions: { can_manage_jobs: true, can_manage_news: true, can_view_analytics: true, can_manage_users: false, can_manage_employers: false } });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const r = await axios.get(`${API}/admin/staff`, { headers: authHeader });
    setAdmins(r.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const create = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/staff`, form, { headers: authHeader });
      toast.success("Admin created"); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setLoading(false);
  };

  const updatePerms = async (id, perms) => {
    try {
      await axios.patch(`${API}/admin/staff/${id}/permissions`, perms, { headers: authHeader });
      toast.success("Permissions updated"); load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-staff">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl font-bold text-slate-900 inline-flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#1A8FE3]" />Staff & permissions</h3>
        {user?.is_superadmin && (
          <Button onClick={() => setOpen(true)} data-testid="staff-add" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full"><Plus className="h-4 w-4 mr-1" />Add admin</Button>
        )}
      </div>
      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-700 uppercase text-xs tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left">Admin</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Role</th>
              {ALL_PERMS.map(p => <th key={p.key} className="px-2 py-3 text-center">{p.label}</th>)}
            </tr>
          </thead>
          <tbody>
            {admins.map(a => (
              <tr key={a.id} className="border-t border-slate-100">
                <td className="px-4 py-3 font-medium">{a.name}</td>
                <td className="px-4 py-3 text-slate-600">{a.email}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full ${a.is_superadmin ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700"}`}>{a.is_superadmin ? "SuperAdmin" : "Admin"}</span>
                </td>
                {ALL_PERMS.map(p => (
                  <td key={p.key} className="px-2 py-3 text-center">
                    <Switch
                      checked={a.is_superadmin ? true : (a.permissions?.[p.key] || false)}
                      disabled={a.is_superadmin || !user?.is_superadmin}
                      onCheckedChange={(v) => updatePerms(a.id, { ...(a.permissions || {}), [p.key]: v })}
                      data-testid={`perm-${a.id}-${p.key}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="staff-dialog">
          <DialogHeader><DialogTitle>Add admin</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input data-testid="staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input data-testid="staff-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>Password</Label><Input data-testid="staff-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              {ALL_PERMS.map(p => (
                <label key={p.key} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                  <span className="text-sm">{p.label}</span>
                  <Switch checked={form.permissions[p.key] || false} onCheckedChange={(v) => setForm({ ...form, permissions: { ...form.permissions, [p.key]: v } })} />
                </label>
              ))}
            </div>
            <Button onClick={create} disabled={loading} data-testid="staff-create" className="w-full bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create admin"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function AggregatorPanel() {
  const { authHeader } = useAuth();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [gcLoading, setGcLoading] = useState(false);

  const loadStats = async () => {
    try {
      const r = await axios.get(`${API}/admin/storage/stats`, { headers: authHeader });
      setStats(r.data);
    } catch (e) {}
  };
  React.useEffect(() => { loadStats(); /* eslint-disable-next-line */ }, []);

  const run = async () => {
    if (!url) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/admin/aggregator/import`, { feed_url: url }, { headers: authHeader });
      toast.success(`Imported ${r.data.imported} jobs`); setUrl("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    setLoading(false);
  };

  const runGc = async () => {
    setGcLoading(true);
    try {
      const r = await axios.post(`${API}/admin/storage/gc`, {}, { headers: authHeader });
      toast.success(`Purged ${r.data.purged_soft_deleted} deleted files, ${r.data.purged_orphans} orphans`);
      loadStats();
    } catch (e) { toast.error(e?.response?.data?.detail || "GC failed"); }
    setGcLoading(false);
  };

  return (
    <div className="space-y-6">
      <Card className="p-5 space-y-3" data-testid="aggregator-panel">
        <h3 className="font-display text-lg font-semibold inline-flex items-center gap-2"><Download className="h-4 w-4 text-[#1A8FE3]" />Import jobs from feed</h3>
        <p className="text-sm text-slate-500">Paste an RSS or JSON feed URL from an <strong>allow-listed</strong> domain. Up to 25 jobs imported into an "External Listings" employer.</p>
        <Input data-testid="agg-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/jobs.rss" />
        <Button onClick={run} disabled={loading} data-testid="agg-run" className="bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Import now"}
        </Button>
      </Card>

      <Card className="p-5 space-y-3" data-testid="storage-panel">
        <h3 className="font-display text-lg font-semibold inline-flex items-center gap-2"><Trash2 className="h-4 w-4 text-[#1A8FE3]" />Object storage</h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="p-4 rounded-xl bg-slate-50">
            <div className="text-xs uppercase tracking-widest text-slate-500">Total files</div>
            <div className="font-display text-2xl font-bold text-slate-900 mt-1">{stats?.total_files ?? "—"}</div>
          </div>
          <div className="p-4 rounded-xl bg-slate-50">
            <div className="text-xs uppercase tracking-widest text-slate-500">Soft-deleted</div>
            <div className="font-display text-2xl font-bold text-rose-700 mt-1">{stats?.soft_deleted ?? "—"}</div>
          </div>
        </div>
        <Button onClick={runGc} disabled={gcLoading} data-testid="gc-run" className="bg-slate-900 hover:bg-slate-800 text-white rounded-full">
          {gcLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run garbage collection"}
        </Button>
        <p className="text-xs text-slate-500">Permanently removes soft-deleted blobs and orphans (files whose owner no longer exists).</p>
      </Card>
    </div>
  );
}
